"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useTranslations } from "next-intl";
import { useState, type default as React } from "react";

import { SUPPORTED_LOCALES, type SiteLocale } from "@trip/constants";
import { cn } from "@trip/utils";

import { CloseIcon } from "~/components/icons/close-icon";
import { HamburgerIcon } from "~/components/icons/hamburger-icon";
import { HR } from "~/components/typography";
import { socialMedia } from "~/lib/social";
import { useCart } from "~/lib/use-cart";
import { Link as LocaleLink, usePathname } from "~/navigation";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = ({ ...props }: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props} />
);
DialogPortal.displayName = DialogPrimitive.Portal.displayName;

const DialogOverlay = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  ref?: React.Ref<React.ComponentRef<typeof DialogPrimitive.Overlay>>;
}) => (
  <DialogPrimitive.Overlay
    className={cn(
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-40 bg-neutral-900/50",
      className,
    )}
    ref={ref}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  ref?: React.RefObject<React.ComponentRef<typeof DialogPrimitive.Content>>;
}) => {
  const tCommon = useTranslations("Common");
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 fixed inset-x-4 top-4 z-50 grid gap-4 rounded-xl bg-white p-6 shadow-lg transition-all duration-200",
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="focus:outline-hidden absolute left-5 top-5 rounded-sm ring-offset-white focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:text-neutral-500">
          <CloseIcon className="size-6" />
          <span className="sr-only">{tCommon("close")}</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
};
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  ref?: React.RefObject<React.ComponentRef<typeof DialogPrimitive.Title>>;
}) => (
  <DialogPrimitive.Title
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    ref={ref}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  ref?: React.RefObject<React.ComponentRef<typeof DialogPrimitive.Description>>;
}) => (
  <DialogPrimitive.Description
    className={cn("text-sm text-neutral-500", className)}
    ref={ref}
    {...props}
  />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export function NavMobile({
  pages,
}: {
  pages: readonly { href: string; name: string }[];
}) {
  const t = useTranslations("Index");
  const tCommon = useTranslations("Common");
  const { cart } = useCart();
  const [open, setOpen] = useState(false);

  const pathnameWithoutLocale =
    "/" +
    usePathname()
      .split("/")
      .slice(1)
      .flatMap((part, index) =>
        index === 0 && SUPPORTED_LOCALES.includes(part as SiteLocale)
          ? []
          : [part],
      )
      .join("/");

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
      }}
    >
      <DialogTrigger asChild aria-label="Site Menu">
        <button type="button" className="size-8 p-0">
          <HamburgerIcon className="size-8" />
        </button>
      </DialogTrigger>
      <DialogOverlay />
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="sr-only">
            {tCommon("siteNavigation")}
          </DialogTitle>
        </DialogHeader>
        <div className="mb-2 flex flex-col items-start gap-4 pt-10">
          {[
            ...pages,
            { href: "/orders", name: t("myOrders"), segment: "orders" },
            {
              href: "/checkout",
              name: (
                <>
                  {tCommon("checkout")}{" "}
                  {cart?.cartLines.length ? (
                    <span className="text-neutral-500">
                      {cart.cartLines.length}
                    </span>
                  ) : null}
                </>
              ),
            },
          ].map(({ href, name }) => (
            <LocaleLink
              href={href}
              key={href}
              onClick={() => {
                setOpen(false);
              }}
            >
              <div className="font-display text-lg font-semibold">{name}</div>
            </LocaleLink>
          ))}
        </div>
        <HR />
        <div className="flex flex-col gap-1.5 text-base">
          {SUPPORTED_LOCALES.map((locale) => (
            <a key={locale} href={`/${locale}${pathnameWithoutLocale}`}>
              {locale === "en" ? "English" : null}
              {locale === "ja" ? "日本語" : null}
              {locale === "ko" ? "한국어" : null}
              {locale === "th" ? "ไทย" : null}
              {locale === "zh-CN" ? "中文" : null}
              {locale === "zh-TW" ? "中文（臺灣）" : null}
            </a>
          ))}
        </div>
        <HR />
        <div className="my-2 flex justify-center gap-6">
          {socialMedia.map(({ href, name, Icon }) => (
            <LocaleLink href={href} target="_blank" key={href}>
              <Icon />
              <span className="sr-only">{name}</span>
            </LocaleLink>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
